import { Box, HStack, Progress, Text } from '@chakra-ui/react';
/* eslint-disable react/no-unstable-nested-components */
import { ColumnDef, PaginationState, createColumnHelper } from '@tanstack/react-table';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import AssignmentsTable from '../tables/AssignmentsTable';
import { ResultGroupEdge } from '@/client/services/api/graphql/gql/graphql';
import WarmNeutralTableActionsMenu from '@/client/components/admin/assignments/tables/WarmNeutralTableActionsMenu';
import { format } from 'date-fns';
import { useAccessHelper } from '@/client/services/hooks/auth/useAccessHelper';
import { useListWorkflowResultGroups } from '../hooks/useListWorkflowResultGroups';
import { useTranslation } from 'react-i18next';
import { useWorkflowActions } from '../hooks/useWorkflowActions';

export default function AssignmentDetailsInstancesView() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    markAssignmentsCompletedMutation,
    sendAssignmentsReminderMutation,
    archiveAssignmentsMutation,
  } = useWorkflowActions();
  const { isAllowed } = useAccessHelper();

  const permissions = {
    edit: isAllowed(['admin', 'assignments', 'edit'], null, ['admin', 'creator', 'manager']),
    view: isAllowed(['admin', 'assignments', 'view'], null, ['admin', 'creator', 'manager']),
    archive: isAllowed(['admin', 'assignments', 'archive'], null, ['admin', 'creator', 'manager']),
    complete: isAllowed(['admin', 'assignments', 'complete'], null, [
      'admin',
      'creator',
      'manager',
    ]),
  };

  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  });

  const pagination = useMemo(
    () => ({
      pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize],
  );

  const { data, refetch, isFetching } = useListWorkflowResultGroups(!!id, pagination, id);

  const getRowId = useCallback((row: any) => row.node.id, []);

  const columnHelper = createColumnHelper<ResultGroupEdge>();

  const columns = useMemo<ColumnDef<ResultGroupEdge, any>[]>(
    () => [
      columnHelper.accessor('node.createdAt', {
        id: 'sentDate',
        header: `${t('admin.assigments.tableHeader.sentDate')}`,
        cell: (info) => (
          <Text
            onClick={(event) => {
              event.stopPropagation();
              navigate(`instance/${info.row.original.node.id}`);
            }}
            _hover={{ textDecoration: 'underline' }}
          >
            {format(new Date(info.getValue()), 'MMM d, yyyy h:mm a')}
          </Text>
        ),
        footer: (info) => info.column.id,
      }),
      columnHelper.accessor('node.totalAssignments', {
        id: 'sentTo',
        header: `${t('admin.assigments.tableHeader.sentTo')}`,
        cell: (info) => (
          <Text color="baseBlack">{`${info.getValue()} ${t(
            'admin.assignments.details.users',
          )}`}</Text>
        ),
        footer: (info) => info.column.id,
      }),
      columnHelper.display({
        id: 'instanceCompletionPercentage',
        header: `${t('admin.assigments.tableHeader.completion')}`,
        cell: (info) => {
          const { completedAssignments, totalAssignments } = info.row.original.node;

          let completionPercentage = 0;

          if (totalAssignments > 0 || completedAssignments > 0) {
            completionPercentage = Math.round((completedAssignments / totalAssignments) * 100);
          }

          return (
            <HStack>
              <Progress variant="assignmentsTable" width="full" value={completionPercentage} />

              <Text>{completionPercentage}%</Text>
            </HStack>
          );
        },
      }),
      columnHelper.accessor('node.createdAt', {
        id: 'createdOn',
        header: `${t('admin.assigments.tableHeader.createdOn')}`,
        cell: (info) => format(new Date(info.getValue()), 'MMM d, yyyy'),
        footer: (info) => info.column,
      }),
      columnHelper.accessor('node.assigner.name', {
        id: 'createdBy',
        header: `${t('admin.assigments.tableHeader.createdBy')}`,
        cell: (info) => <Text color="baseBlack">{info.getValue()}</Text>,
        footer: (info) => info.column.id,
      }),

      columnHelper.display({
        id: 'actions',
        cell: (info) => {
          const { node } = info.row.original;

          const payload = { groupIds: [node.id] };

          const isComplete = node.completedAssignments === node.totalAssignments;

          return (
            <WarmNeutralTableActionsMenu
              actions={[
                {
                  label: t('admin.assignments.tableActions.markComplete'),
                  shouldRender: !isComplete && permissions.complete,
                  onClick: () =>
                    markAssignmentsCompletedMutation.mutateAsync(payload).then(() => refetch()),
                },
                {
                  label: t('admin.assignments.tableActions.sendReminder'),
                  shouldRender: !isComplete && (permissions.edit || permissions.view),
                  onClick: () =>
                    sendAssignmentsReminderMutation.mutateAsync(payload).then(() => refetch()),
                },
                {
                  label: t('admin.assignments.tableActions.unassign'),
                  shouldRender: !isComplete && permissions.archive,
                  onClick: () =>
                    archiveAssignmentsMutation.mutateAsync(payload).then(() => refetch()),
                },
              ]}
            />
          );
        },
      }),
    ],
    [],
  );

  return (
    <Box marginBottom={50}>
      <AssignmentsTable
        isLoading={isFetching}
        columns={columns}
        data={data?.edges}
        getRowId={getRowId}
        pagination={pagination}
        setPagination={setPagination}
        totalResultCount={data?.pageInfo.totalCount ?? 0}
        onRowClick={(row) => navigate(`instance/${row.id}`)}
      />
    </Box>
  );
}
